import React, { useId } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { JustInfo } from "../../utils/Icon";

const SensitiveDataAlert = ({ fieldCount, rowCount, sx, guidance }) => {
  if (!fieldCount) return null;

  return (
    <Alert severity='warning' sx={{ mb: 2, ...sx }}>
      <Typography variant='subtitle2' fontWeight={700}>
        Redacted sensitive data needs review
      </Typography>
      <Typography variant='body2'>
        {guidance || (
          <>
            {fieldCount} value{fieldCount === 1 ? "" : "s"} across {rowCount}{" "}
            line item{rowCount === 1 ? "" : "s"} were detected as sensitive and
            replaced with red placeholders like{" "}
            <Box
              component='span'
              sx={{ color: "error.main", fontWeight: 700, fontStyle: "italic" }}
            >
              {"<like this>"}
            </Box>
            . Check each highlighted field and restore the original name or
            amount so nothing is missing before you continue.
          </>
        )}
      </Typography>
    </Alert>
  );
};

const CheckboxWithInfo = ({ checked, disabled, onChange, label, tooltip }) => (
  <FormControlLabel
    disabled={disabled}
    control={
      <Checkbox
        size='small'
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
    }
    label={
      <Stack
        direction='row'
        alignItems='center'
        spacing={0.5}
        sx={{ minWidth: 0, width: "100%" }}
      >
        <Typography variant='body2' noWrap sx={{ flex: 1 }}>
          {label}
        </Typography>
        {tooltip && (
          <Box component='span' sx={{ display: "inline-flex", flexShrink: 0 }}>
            <JustInfo title={tooltip} ml={0} mr={0} />
          </Box>
        )}
      </Stack>
    }
    sx={{
      m: 0,
      width: "100%",
      "& .MuiFormControlLabel-label": {
        flex: 1,
        minWidth: 0
      }
    }}
  />
);

/**
 * Controlled mapping sidebar. Each processing option supplies
 * { id, label, checked, onChange, disabled?, tooltip? }.
 * Engines own task dependencies, validation and any extra processing controls.
 */
const MappingSettings = ({
  title = "Column Mapping",
  description = "Map each extracted column to the correct field.",
  onBack,
  sensitiveData = {},
  sheets = [],
  selectedSheetName = "",
  onSheetChange,
  processingOptions = [],
  processingDescription = "Choose the tasks for this run.",
  processingControls,
  processingError,
  assignmentWarning,
  includedCount = 0,
  canProceed = false,
  busy = false,
  proceedHint,
  onProceed,
  onReset,
  sx = {}
}) => {
  const sheetLabelId = useId();

  return (
    <Paper
      variant='outlined'
      sx={{
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        maxHeight: { xs: "none", md: "100%" },
        minHeight: 0,
        overflow: "auto",
        p: { xs: 1.25, sm: 1.5 },
        width: { xs: "100%", md: 300, lg: 320 },
        ...sx
      }}
    >
      <Box sx={{ mb: 1.25 }}>
        <Stack
          direction='row'
          alignItems='flex-start'
          justifyContent='space-between'
          spacing={1}
        >
          <Typography variant='h6' fontWeight={700} lineHeight={1.2}>
            {title}
          </Typography>
          {onBack && (
            <Button
              onClick={onBack}
              size='small'
              sx={{ flexShrink: 0, textTransform: "none" }}
            >
              Back
            </Button>
          )}
        </Stack>
        <Typography
          color='text.secondary'
          variant='caption'
          sx={{
            display: { xs: "none", xl: "block" },
            mt: 0.5,
            width: "100%"
          }}
        >
          {description}
        </Typography>
      </Box>

      <SensitiveDataAlert {...sensitiveData} />

      {sheets.length > 1 && (
        <FormControl fullWidth size='small' sx={{ mb: 1.25 }}>
          <InputLabel id={sheetLabelId}>Table</InputLabel>
          <Select
            labelId={sheetLabelId}
            label='Table'
            value={selectedSheetName}
            onChange={(event) => onSheetChange?.(event.target.value)}
          >
            {sheets.map((sheet) => (
              <MenuItem key={sheet.name} value={sheet.name}>
                {sheet.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {(processingOptions.length > 0 || processingControls) && (
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            p: 1.25
          }}
        >
          <Typography
            variant='caption'
            color='text.secondary'
            fontWeight={700}
          >
            AI processing
          </Typography>
          {processingDescription && (
            <Typography
              variant='caption'
              color='text.secondary'
              display='block'
              sx={{ display: { xs: "none", xl: "block" }, mt: 0.25, mb: 0.75 }}
            >
              {processingDescription}
            </Typography>
          )}
          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            {processingOptions.map(({ id, ...option }) => (
              <CheckboxWithInfo key={id} {...option} />
            ))}
          </Stack>
          {processingControls}
          {processingError && (
            <Typography
              variant='caption'
              sx={{ color: "error.main", display: "block", mt: 0.75 }}
            >
              {processingError}
            </Typography>
          )}
        </Box>
      )}

      <Box sx={{ mt: 1.25 }}>
        {assignmentWarning && (
          <Alert
            severity='info'
            variant='outlined'
            sx={{
              alignItems: "center",
              textAlign: "justify",
              mb: 1,
              py: 0.25,
              "& .MuiAlert-icon": {
                mr: 0.75,
                py: 0
              },
              "& .MuiAlert-message": {
                py: 0
              }
            }}
          >
            <Typography variant='caption'>{assignmentWarning}</Typography>
          </Alert>
        )}
        <Tooltip
          title={!canProceed ? proceedHint : undefined}
          placement='top'
        >
          <span>
            <Button
              fullWidth
              variant='contained'
              disabled={!canProceed || busy}
              onClick={onProceed}
              sx={{ textTransform: "none" }}
            >
              Proceed with {includedCount} Selected{" "}
              {includedCount === 1 ? "Row" : "Rows"}
            </Button>
          </span>
        </Tooltip>
        {onReset && (
          <Button
            fullWidth
            color='inherit'
            size='small'
            startIcon={<RestartAltIcon fontSize='small' />}
            onClick={onReset}
            disabled={busy}
            sx={{ mt: 0.5, textTransform: "none" }}
          >
            Reset to original extract
          </Button>
        )}
      </Box>
    </Paper>
  );
};

export { SensitiveDataAlert };
export default MappingSettings;
